import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Sparkles, Loader2 } from "lucide-react";

export default function Testimony() {
  const [form, setForm] = useState({ before_text: "", how_text: "", since_text: "" });
  const [coach, setCoach] = useState("");
  const [coaching, setCoaching] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/testimony").then(({ data }) => setForm(data));
  }, []);

  const save = async () => {
    setSaving(true);
    await api.post("/testimony", form);
    setSaving(false);
    toast.success("Testimony saved.");
  };

  const polish = async () => {
    const text = `BEFORE: ${form.before_text}\n\nHOW: ${form.how_text}\n\nSINCE: ${form.since_text}`;
    if (text.length < 30) return toast.error("Write more of your story first.");
    setCoaching(true);
    setCoach("");
    try {
      const { data } = await api.post("/coach", { text, kind: "testimony" });
      setCoach(data.reply);
    } catch (e) {
      toast.error("Coach unavailable.");
    } finally {
      setCoaching(false);
    }
  };

  const parts = [
    ["before_text", "Before", "What did your life or heart look like without Christ at the center?"],
    ["how_text", "How", "The moment or season you began to trust, follow, and be shaped by Him."],
    ["since_text", "Since", "What's different now — in ordinary, honest terms, including what's still in progress."],
  ];

  return (
    <div className="max-w-3xl mx-auto">
      <p className="text-xs uppercase tracking-[0.2em] text-primary">Week 10 · Telling Your Story</p>
      <h1 className="font-serif text-5xl mt-3 leading-tight">Your testimony</h1>
      <p className="text-muted-foreground mt-4 max-w-2xl">
        A testimony is an experience to tell, not an argument to win. Keep it short — two to three minutes.
        The most powerful part is usually the most honest one, not the most polished.
      </p>

      <div className="space-y-10 mt-14">
        {parts.map(([k, label, hint], i) => (
          <section key={k} className="reveal" style={{ animationDelay: `${i * 60}ms` }}>
            <div className="flex items-baseline gap-4 mb-3">
              <span className="font-serif text-2xl text-primary tabular-nums">{String(i + 1).padStart(2, "0")}</span>
              <h2 className="font-serif text-2xl">{label}</h2>
            </div>
            <p className="text-sm text-muted-foreground pl-12 mb-3 italic">{hint}</p>
            <Textarea data-testid={`testimony-${k}`} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
              rows={5} className="bg-transparent border-x-0 border-t-0 border-b rounded-none focus-visible:ring-0 focus-visible:border-primary" />
          </section>
        ))}
      </div>

      <div className="flex flex-wrap gap-3 mt-10">
        <Button onClick={save} disabled={saving} data-testid="testimony-save" className="rounded-full">
          {saving ? "Saving…" : "Save my testimony"}
        </Button>
        <Button onClick={polish} disabled={coaching} variant="outline" data-testid="testimony-polish" className="rounded-full">
          {coaching ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Reading…</> : <><Sparkles className="w-4 h-4 mr-2" /> Ask coach to gently polish</>}
        </Button>
      </div>

      {coach && (
        <div className="mt-10 border-l-2 border-accent pl-5 py-2 reveal" data-testid="testimony-coach-reply">
          <div className="text-xs uppercase tracking-widest text-accent mb-2">Reflection coach</div>
          <p className="whitespace-pre-wrap leading-relaxed">{coach}</p>
        </div>
      )}
    </div>
  );
}
